import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { TrainingApi } from "../../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
    Box,
    CircularProgress,
    Container,
    ThemeProvider,
    CssBaseline,
} from "@mui/material";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import NavBar from "../../layout/NavBar";
import NavBarBot from "../../layout/NavBarBot";
import LoadingNavBarBot from "../../layout/LoadingNavBarBot";
import HeaderCard from "../../layout/HeaderCard";
import Notification from "../../util/notifications/Notification";
import { darkTheme } from "../../../theme/darkTheme";
import { INITIAL_DATA } from "./components/eigeneUebungen/constants";
import UebungForm from "./components/eigeneUebungen/UebungForm";
import UebungenListe from "./components/eigeneUebungen/UebungenListe";

function NutzerEigeneUebungenPage() {
    const { nutzer, isLoggedIn, loading: authLoading } = useAuth();

    const [data, setData] = useState(INITIAL_DATA);
    const [uebungen, setUebungen] = useState([]);
    const [loading, setLoading] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const [submitError, setSubmitError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [message, setMessage] = useState({ type: "", text: "" });
    const [showNotification, setShowNotification] = useState(false);

    const loadUebungen = useCallback(async () => {
        if (!nutzer?.id) return;
        setListLoading(true);
        try {
            const response = await TrainingApi.getUebungenByUserId(nutzer.id);
            setUebungen(response.data ?? []);
        } catch (err) {
            console.error("Fehler beim Laden der Übungen:", err);
            setMessage({ type: "error", text: "Übungen konnten nicht geladen werden." });
            setShowNotification(true);
        } finally {
            setListLoading(false);
        }
    }, [nutzer?.id]);

    useEffect(() => {
        loadUebungen();
    }, [loadUebungen]);

    const validate = () => {
        const errors = {};
        if (!data.name.trim()) errors.name = "Name ist erforderlich.";
        if (!data.kategorie) errors.kategorie = "Kategorie ist erforderlich.";
        return errors;
    };

    const handleChange = (field) => (e) => {
        setData((prev) => ({ ...prev, [field]: e.target.value }));
        if (fieldErrors[field]) {
            setFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSubmitError("");

        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setLoading(true);
        try {
            await TrainingApi.postCreateUserUebung({
                name: data.name.trim(),
                beschreibung: data.beschreibung.trim() || null,
                zielmuskel: data.zielmuskel || null,
                kategorie: data.kategorie,
            });
            setData(INITIAL_DATA);
            setFieldErrors({});
            setMessage({ type: "success", text: "Übung erfolgreich gespeichert!" });
            setShowNotification(true);
            await loadUebungen();
        } catch (err) {
            console.error("Fehler beim Speichern:", err);
            const apiMessage = err.response?.data?.error;
            setSubmitError(
                apiMessage || "Speichern fehlgeschlagen. Bitte erneut versuchen."
            );
        } finally {
            setLoading(false);
        }
    };

    if (authLoading) {
        return (
            <ThemeProvider theme={darkTheme}>
                <CssBaseline />
                <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                    <CircularProgress />
                </Box>
            </ThemeProvider>
        );
    }

    if (!isLoggedIn) {
        return <Navigate to="/login" replace />;
    }

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <NavBar />
            <Container maxWidth="md" sx={{ py: 2, pb: 10 }}>
                <HeaderCard
                    title="Eigene Übungen"
                    subtitle="Lege persönliche Übungen an, die nur in deinem Konto sichtbar sind."
                    icon={<FitnessCenterIcon />}
                />

                <UebungForm
                    data={data}
                    fieldErrors={fieldErrors}
                    submitError={submitError}
                    loading={loading}
                    onChange={handleChange}
                    onSubmit={handleSave}
                />

                <UebungenListe uebungen={uebungen} listLoading={listLoading} />
            </Container>

            {nutzer ? <NavBarBot /> : <LoadingNavBarBot />}

            {showNotification && message.text && (
                <Notification
                    type={message.type}
                    message={message.text}
                    onClose={() => {
                        setShowNotification(false);
                        setMessage({ type: "", text: "" });
                    }}
                />
            )}
        </ThemeProvider>
    );
}

export default NutzerEigeneUebungenPage;
